import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { createVendor, updateVendor } from 'services/vendor-service';
import { Dialog, Checkbox, Select, TextField, Button } from 'Components/ui';

const INK = 'var(--vo-ink)';
const LABEL_COLOR = 'var(--vo-ink-2)';
const MUTED = 'var(--vo-slate)';
const ERROR = 'var(--vo-danger)';
const SUCCESS = 'var(--vo-success)';

const RISK_STYLES = {
  1: { bg: 'var(--vo-danger-10)', border: 'var(--vo-danger-20)', color: ERROR   },
  2: { bg: 'var(--vo-heatmap-risky)', border: 'var(--vo-warning-15)', color: 'var(--vo-warning)' },
  3: { bg: 'var(--vo-success-10)', border: 'var(--vo-success-15)', color: SUCCESS },
};

const SectionCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
  padding: 28px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const IconBox = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $gradient, $bg }) => $gradient || $bg || 'var(--vo-cloud)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SectionHeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SectionTitle = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  line-height: 1.2;
`;

const SectionSubtitle = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;

const SectionBody = styled.div`
  padding: 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

const FieldGroup = styled.div``;

const FieldLabel = styled.label`
  display: block;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${LABEL_COLOR};
  margin-bottom: 6px;
`;

const RequiredMark = styled.span`
  color: ${ERROR};
  margin-left: 2px;
`;

const HelpText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 6px 0 0;
`;

const RiskResultBox = styled.div`
  background: ${({ $tier }) => RISK_STYLES[$tier]?.bg || 'var(--vo-mist)'};
  border: 1px solid ${({ $tier }) => RISK_STYLES[$tier]?.border || 'var(--vo-line)'};
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const RiskScoreCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const RiskScoreLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .07em;
`;

const RiskScoreNum = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: ${({ $tier }) => RISK_STYLES[$tier]?.color || MUTED};
  letter-spacing: -0.02em;
  line-height: 1;
`;

const RiskTierDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 4px 0 0;
  text-align: right;
`;

const TierChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: 20px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $tier }) => RISK_STYLES[$tier]?.bg || 'var(--vo-mist)'};
  color: ${({ $tier }) => RISK_STYLES[$tier]?.color || MUTED};
  border: 1px solid ${({ $tier }) => RISK_STYLES[$tier]?.border || 'var(--vo-line)'};
`;

const DpaCard = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: 16px;
  background: var(--vo-success-10);
  border-radius: 10px;
  border: 1px solid var(--vo-success-15);
`;

const DpaCardTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${LABEL_COLOR};
  margin: 0 0 3px;
`;

const DpaDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
  line-height: 1.5;
`;

const WarningAlert = styled.div`
  padding: 12px 16px;
  background: var(--vo-heatmap-risky);
  border: 1px solid color-mix(in srgb, var(--vo-warning) 30%, transparent);
  border-radius: 10px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${INK};
  margin-top: 16px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 8px;
`;

const PersonSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const ShieldSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const CheckSVG = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--vo-success)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

export const VendorForm = ({ vendor, onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['vendor', 'common']);
  const isEditMode = !!vendor;

  const [formData, setFormData] = useState({
    name: '',
    contactEmail: '',
    contactName: '',
    relationshipDescription: '',
    dataSensitivity: 1,
    businessCriticality: 1,
    dpaEstablished: false,
    dpaExpiryDate: '',
    dpaDocumentReference: '',
    recertificationPeriodMonths: '',
  });

  const [inherentRiskScore, setInherentRiskScore] = useState(1);
  const [tier, setTier] = useState(3);

  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name || '',
        contactEmail: vendor.contactEmail || '',
        contactName: vendor.contactName || '',
        relationshipDescription: vendor.relationshipDescription || '',
        dataSensitivity: vendor.dataSensitivity || 1,
        businessCriticality: vendor.businessCriticality || 1,
        dpaEstablished: vendor.dpaEstablished || false,
        // <input type="date"> takes YYYY-MM-DD
        dpaExpiryDate: vendor.dpaExpiryDate ? String(vendor.dpaExpiryDate).slice(0, 10) : '',
        dpaDocumentReference: vendor.dpaDocumentReference || '',
        recertificationPeriodMonths: vendor.recertificationPeriodMonths || '',
      });
    }
  }, [vendor]);

  useEffect(() => {
    const riskScore = Math.max(formData.dataSensitivity, formData.businessCriticality);
    setInherentRiskScore(riskScore);
    if (riskScore <= 2) setTier(3);
    else if (riskScore <= 4) setTier(2);
    else setTier(1);
  }, [formData.dataSensitivity, formData.businessCriticality]);

  const mutation = useMutation(
    (data) => isEditMode ? updateVendor(vendor._id, data) : createVendor(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('vendors');
        Dialog.fire({
          icon: 'success',
          title: isEditMode ? t('vendor:notifications.updatedTitle') : t('vendor:notifications.createdTitle'),
          text: isEditMode ? t('vendor:notifications.updatedText') : t('vendor:notifications.createdText'),
          timer: 2000,
          showConfirmButton: false,
        });
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        Dialog.alert({
          icon: 'error',
          title: t('vendor:notifications.errorTitle'),
          message: error.response?.data?.error || error.response?.data?.message || t('vendor:notifications.saveError'),
        });
      },
    }
  );

  const handleInputChange = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });
  const handleSelectChange = (field) => (value) => setFormData({ ...formData, [field]: value });
  const handleCheckboxChange = (field) => (e) => setFormData({ ...formData, [field]: e.target.checked });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Every vendor requires a DPA (not only dataSensitivity >= 4) — same rule as the dashboard card;
    // the Restricted/PII wording is kept for levels 4-5 only.
    if (!formData.dpaEstablished) {
      const result = await Dialog.fire({
        icon: 'warning',
        title: t('vendor:notifications.noDpaTitle'),
        html: [
          formData.dataSensitivity >= 4
            ? t('vendor:notifications.noDpaDataDesc', { level: formData.dataSensitivity })
            : t('vendor:notifications.noDpaDataDescGeneral', { level: formData.dataSensitivity }),
          t('vendor:notifications.noDpaLegalReq'),
          t('vendor:notifications.noDpaQuestion'),
        ].join('<br/><br/>'),
        showCancelButton: true,
        confirmButtonText: t('vendor:notifications.noDpaConfirm'),
        cancelButtonText: t('vendor:notifications.noDpaCancel'),
        confirmButtonColor: ERROR,
      });
      if (!result.isConfirmed) return;
    }
    const { dpaExpiryDate, dpaDocumentReference, ...rest } = formData;
    const dpaReference = dpaDocumentReference.trim();
    mutation.mutate({
      ...rest,
      active: true,
      recertificationPeriodMonths: formData.recertificationPeriodMonths
        ? parseInt(formData.recertificationPeriodMonths, 10)
        : undefined,
      // DPA details only exist while a DPA is established; on edit, null/'' clears them.
      ...(formData.dpaEstablished
        ? {
            ...(dpaExpiryDate ? { dpaExpiryDate } : isEditMode ? { dpaExpiryDate: null } : {}),
            ...(dpaReference ? { dpaDocumentReference: dpaReference } : isEditMode ? { dpaDocumentReference: '' } : {}),
          }
        : isEditMode
          ? { dpaExpiryDate: null, dpaDocumentReference: '' }
          : {}),
    });
  };

  const getTierLabel = (tierValue) =>
    t(`vendor:tier.${String(tierValue)}.fullLabel`, { defaultValue: t('vendor:tier.unknown.fullLabel') });

  return (
    <form onSubmit={handleSubmit}>
      <SectionCard>
        <SectionHeader>
          <IconBox $gradient="linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))">
            <PersonSVG />
          </IconBox>
          <SectionHeadText>
            <SectionTitle>{t('vendor:form.sections.info')}</SectionTitle>
            <SectionSubtitle>{t('vendor:form.sections.infoSubtitle')}</SectionSubtitle>
          </SectionHeadText>
        </SectionHeader>
        <SectionBody>
          <FormGrid>
            <FieldGroup>
              <FieldLabel htmlFor="name">
                {t('vendor:form.fields.name')}<RequiredMark>*</RequiredMark>
              </FieldLabel>
              <TextField
                id="name"
                required
                fullWidth
                value={formData.name}
                onChange={handleInputChange('name')}
                placeholder={t('vendor:form.fields.namePlaceholder')}
              />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel htmlFor="contactEmail">
                {t('vendor:form.fields.contactEmail')}<RequiredMark>*</RequiredMark>
              </FieldLabel>
              <TextField
                id="contactEmail"
                type="email"
                required
                fullWidth
                value={formData.contactEmail}
                onChange={handleInputChange('contactEmail')}
                placeholder={t('vendor:form.fields.contactEmailPlaceholder')}
              />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel htmlFor="contactName">
                {t('vendor:form.fields.contactName')}
              </FieldLabel>
              <TextField
                id="contactName"
                fullWidth
                value={formData.contactName}
                onChange={handleInputChange('contactName')}
                placeholder={t('vendor:form.fields.contactNamePlaceholder')}
              />
            </FieldGroup>

            <FullWidth>
              <FieldGroup>
                <FieldLabel htmlFor="relationshipDescription">
                  {t('vendor:form.fields.relationshipDescription')}
                </FieldLabel>
                <TextField
                  id="relationshipDescription"
                  multiline
                  fullWidth
                  rows={3}
                  value={formData.relationshipDescription}
                  onChange={handleInputChange('relationshipDescription')}
                  placeholder={t('vendor:form.fields.relationshipPlaceholder')}
                />
              </FieldGroup>
            </FullWidth>
          </FormGrid>
        </SectionBody>
      </SectionCard>

      <SectionCard>
        <SectionHeader>
          <IconBox $gradient="linear-gradient(135deg,var(--vo-danger),var(--vo-warning))">
            <ShieldSVG />
          </IconBox>
          <SectionHeadText>
            <SectionTitle>{t('vendor:form.sections.risk')}</SectionTitle>
            <SectionSubtitle>{t('vendor:form.sections.riskSubtitle')}</SectionSubtitle>
          </SectionHeadText>
        </SectionHeader>
        <SectionBody>
          <FormGrid>
            <FieldGroup>
              <FieldLabel htmlFor="dataSensitivity">
                {t('vendor:form.fields.dataSensitivity')}<RequiredMark>*</RequiredMark>
              </FieldLabel>
              <Select
                name="dataSensitivity"
                value={formData.dataSensitivity}
                onChange={handleSelectChange('dataSensitivity')}
                options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: t(`vendor:form.dataSensitivity.${n}`) }))}
              />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel htmlFor="businessCriticality">
                {t('vendor:form.fields.businessCriticality')}<RequiredMark>*</RequiredMark>
              </FieldLabel>
              <Select
                name="businessCriticality"
                value={formData.businessCriticality}
                onChange={handleSelectChange('businessCriticality')}
                options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: t(`vendor:form.businessCriticality.${n}`) }))}
              />
            </FieldGroup>

            <FullWidth>
              <RiskResultBox $tier={tier}>
                <RiskScoreCol>
                  <RiskScoreLabel>{t('vendor:form.riskScore.label')}</RiskScoreLabel>
                  <RiskScoreNum $tier={tier}>{inherentRiskScore} {t('vendor:form.riskScore.outOf')}</RiskScoreNum>
                </RiskScoreCol>
                <div style={{ textAlign: 'right' }}>
                  <TierChip $tier={tier}>{getTierLabel(tier)}</TierChip>
                  <RiskTierDesc>{t(`vendor:tier.${String(tier)}.description`)}</RiskTierDesc>
                </div>
              </RiskResultBox>
            </FullWidth>
          </FormGrid>
        </SectionBody>
      </SectionCard>

      <SectionCard>
        <SectionHeader>
          <IconBox $bg="var(--vo-success-15)">
            <CheckSVG />
          </IconBox>
          <SectionHeadText>
            <SectionTitle>{t('vendor:form.sections.compliance')}</SectionTitle>
            <SectionSubtitle>{t('vendor:form.sections.complianceSubtitle')}</SectionSubtitle>
          </SectionHeadText>
        </SectionHeader>
        <SectionBody>
          <FormGrid>
            <FullWidth>
              <DpaCard>
                <Checkbox
                  checked={formData.dpaEstablished}
                  onChange={handleCheckboxChange('dpaEstablished')}
                />
                <div>
                  <DpaCardTitle>{t('vendor:form.fields.dpaTitle')}</DpaCardTitle>
                  <DpaDesc>{t('vendor:form.fields.dpaDescription')}</DpaDesc>
                </div>
              </DpaCard>
              {!formData.dpaEstablished && (
                <WarningAlert>
                  {t(formData.dataSensitivity >= 4 ? 'vendor:form.alerts.dpaWarning' : 'vendor:form.alerts.dpaWarningGeneral')}
                </WarningAlert>
              )}
            </FullWidth>

            {formData.dpaEstablished && (
              <>
                <FieldGroup>
                  <FieldLabel htmlFor="dpaDocumentReference">
                    {t('vendor:form.fields.dpaDocumentReference')}
                  </FieldLabel>
                  <TextField
                    id="dpaDocumentReference"
                    fullWidth
                    value={formData.dpaDocumentReference}
                    onChange={handleInputChange('dpaDocumentReference')}
                    inputProps={{ maxLength: 500 }}
                    placeholder={t('vendor:form.fields.dpaDocumentReferencePlaceholder')}
                  />
                  <HelpText>{t('vendor:form.fields.dpaDocumentReferenceHelp')}</HelpText>
                </FieldGroup>

                <FieldGroup>
                  <FieldLabel htmlFor="dpaExpiryDate">
                    {t('vendor:form.fields.dpaExpiryDate')}
                  </FieldLabel>
                  <TextField
                    id="dpaExpiryDate"
                    type="date"
                    fullWidth
                    value={formData.dpaExpiryDate}
                    onChange={handleInputChange('dpaExpiryDate')}
                  />
                  <HelpText>{t('vendor:form.fields.dpaExpiryDateHelp')}</HelpText>
                </FieldGroup>
              </>
            )}

            <FieldGroup>
              <FieldLabel htmlFor="recertificationPeriodMonths">
                {t('vendor:form.fields.recertificationPeriod')}
              </FieldLabel>
              <TextField
                id="recertificationPeriodMonths"
                type="number"
                fullWidth
                value={formData.recertificationPeriodMonths}
                onChange={handleInputChange('recertificationPeriodMonths')}
                inputProps={{ min: 1, max: 60 }}
                placeholder={t('vendor:form.fields.recertificationPlaceholder')}
              />
              <HelpText>{t('vendor:form.fields.recertificationHelp')}</HelpText>
            </FieldGroup>

            <FullWidth>
              <ActionRow>
                {onCancel && (
                  <Button variant="outline" type="button" onClick={onCancel} disabled={mutation.isLoading} sx={{ height: '44px', padding: '0 20px' }}>
                    {t('vendor:form.actions.back')}
                  </Button>
                )}
                <Button
                  variant="primary"
                  type="submit"
                  disabled={mutation.isLoading}
                  isLoading={mutation.isLoading}
                  sx={{ height: '40px', padding: '0 24px' }}
                >
                  {mutation.isLoading
                    ? t('vendor:form.actions.saving')
                    : isEditMode
                      ? t('vendor:form.actions.update')
                      : t('vendor:form.actions.create')}
                </Button>
              </ActionRow>
            </FullWidth>
          </FormGrid>
        </SectionBody>
      </SectionCard>
    </form>
  );
};
